import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Download, ShoppingCart } from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const TheaterMode = ({
  isOpen,
  onClose,
  assets,
  currentIndex,
  onIndexChange,
  galleryId,
  apiUrl,
  magicLinkToken,
  isPublic = false,
  onAddToCart,
  onDownload,
  disableRightClick = false
}) => {
  const currentAsset = assets[currentIndex];

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  const handleNext = useCallback(() => {
    if (currentIndex < assets.length - 1) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, assets.length, onIndexChange]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, onClose]);

  // Prevent body scroll when theater mode is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle right-click prevention for public galleries
  const handleContextMenu = (e) => {
    if (disableRightClick) {
      e.preventDefault();
      return false;
    }
  };

  const getImageUrl = (asset) => {
    const baseUrl = `${apiUrl}/media/${galleryId}/${asset.filename}`;
    if (magicLinkToken) {
      return `${baseUrl}?token=${magicLinkToken}`;
    }
    return baseUrl;
  };

  if (!isOpen || !currentAsset) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95"
        onContextMenu={handleContextMenu}
        data-testid="theater-mode"
      >
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
          onClick={onClose}
          data-testid="theater-close"
        >
          <X className="w-6 h-6" />
        </Button>

        {/* Navigation - Previous */}
        {currentIndex > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 w-12 h-12"
            onClick={handlePrevious}
            data-testid="theater-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
        )}

        {/* Navigation - Next */}
        {currentIndex < assets.length - 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 w-12 h-12"
            onClick={handleNext}
            data-testid="theater-next"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        )}

        {/* Main Image */}
        <div className="flex items-center justify-center h-[calc(100vh-120px)] pt-16 overflow-hidden">
          <motion.img
            key={currentAsset.asset_id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            src={getImageUrl(currentAsset)}
            alt={currentAsset.filename}
            className="max-h-full max-w-full object-contain select-none"
            draggable={false}
            onContextMenu={handleContextMenu}
          />
          
          {/* Watermark for public galleries */}
          {isPublic && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-white/10 text-6xl font-serif rotate-[-30deg] select-none">
                PREVIEW
              </span>
            </div>
          )}
        </div>

        {/* Bottom Controls & Thumbnail Strip */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent">
          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-4 py-3">
            {/* Private Gallery - Download Button */}
            {!isPublic && onDownload && (
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => onDownload(currentAsset)}
                data-testid="theater-download"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}

            {/* Public Gallery - Add to Cart Button with Price Tooltip */}
            {isPublic && currentAsset.price && onAddToCart && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => onAddToCart(currentAsset)}
                      data-testid="theater-add-cart"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Add to Cart
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-primary text-primary-foreground">
                    <p>${currentAsset.price.toFixed(2)}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {isPublic && !currentAsset.price && (
              <span className="text-white/60 text-sm">Preview Only</span>
            )}
          </div>

          {/* Thumbnail Strip */}
          <div className="flex items-center justify-center gap-2 px-4 pb-4 overflow-x-auto max-w-full">
            {assets.map((asset, index) => (
              <button
                key={asset.asset_id}
                onClick={() => onIndexChange(index)}
                className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                  index === currentIndex
                    ? "border-primary scale-110"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
                data-testid={`theater-thumb-${index}`}
              >
                <img
                  src={getImageUrl(asset)}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </button>
            ))}
          </div>

          {/* Photo Counter */}
          <div className="text-center pb-4">
            <span className="text-white/60 text-sm">
              {currentIndex + 1} of {assets.length}
            </span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TheaterMode;
